import React, { useEffect, useRef, useState } from "react";
import { FaceitLevel } from "./FaceitLevel";
import { useSnackbar } from "../contexts/SnackbarProvider";
import Play from "../icons/Play";
import dayjs from "dayjs";

const requester = new TitarRequester();

interface WatchlistPlayerProps {
    player: {
        id: number;
        faceit_player_id: string;
        nickname: string;
        avatar: string;
        faceit_url: string;
        country: string;
        skill_level: number;
        elo: number;
        last_game_at: string;
        ban: string | null;
        status: string;
        score?: string;
        match_type?: string;
        match_id?: string;
        match_url?: string;
    };
    game: {
        score?: string;
        url?: string;
        type?: string;
        name?: string;
    }
    onRemove: (playerId: number) => void;
    setWatchlist: React.Dispatch<React.SetStateAction<any>>;
    setGames: React.Dispatch<React.SetStateAction<any>>;
}

export function getNextUpdateDelaySeconds({
    status,
    lastFinishedAt,
    now = Math.floor(Date.now() / 1000),
}: {
    status: string,
    lastFinishedAt: number | null,
    now?: number,
}): number {
    // если игрок сейчас играет — обновляем часто
    if (status === "Ongoing") {
        return 60 * 1000; // золотая середина между 30–60
    }

    // если нет данных о матчах
    if (!lastFinishedAt) {
        return 900 * 1000; // 15 минут
    }

    const secondsAgo = now - lastFinishedAt;

    // защита от кривых данных
    if (secondsAgo < 0) {
        return 300 * 1000;
    }

    // матч закончился недавно (< 1 часа)
    if (secondsAgo < 60 * 60) {
        return 120 * 1000; // 2 минуты
    }

    // 1–24 часа
    if (secondsAgo < 60 * 60 * 24) {
        return 600 * 1000; // 10 минут
    }

    // больше суток
    return 900 * 1000; // 15 минут
}

export const WatchlistPlayer = ({ player, game, onRemove, setWatchlist, setGames }: WatchlistPlayerProps) => {
    const matchTimeoutRef = useRef<number | null>(null);
    const removePlayer = () => {
        onRemove(player.id)
    }

    const getMatch = async () => {
        return requester.get('/api/faceit/match/' + player.match_id).then((response: any) => {
            if (response.status === 'FINISHED') {
                const data = new FormData();
                data.append('last_game_at', response.finished_at ? dayjs.unix(response.finished_at).toISOString() : '');
                requester.post('/api/faceit/update/' + player.faceit_player_id, data);

                setWatchlist((prevWatchlist: any) => prevWatchlist.map((p: any) => {
                    if (p.id === player.id) {
                        const newPlayer = { ...p };
                        newPlayer.status = "Finished";
                        newPlayer.last_game_at = response.finished_at ? dayjs.unix(response.finished_at).toISOString() : newPlayer.last_game_at;
                        newPlayer.match_id = undefined;
                        return newPlayer;
                    }

                    return p;
                }).sort((a: any, b: any) => a.last_game_at === b.last_game_at ? 0 : a.last_game_at < b.last_game_at ? 1 : -1));

            } else {
                setGames((prevGames: any) => ({
                    ...prevGames,
                    [player.id]: {
                        score: response.results ? response.results.score.faction1 + ':' + response.results.score.faction2 : null,
                        url: response.faceit_url.replace('{lang}', 'en'),
                        type: response.competition_type,
                        name: response.competition_name,
                    }
                }));
            }
        })
    }

    const getOngoingMatch = async () => {
        return requester.get('/api/faceit/ongoing/' + player.faceit_player_id).then((response: any) => {
            setWatchlist((prevWatchlist: any) => prevWatchlist.map((p: any) => {
                if (p.id === player.id) {
                    const newPlayer = { ...p };

                    if (response.match_id) {
                        newPlayer.match_id = response.match_id;
                        newPlayer.last_game_at = response.last_game_at ?? newPlayer.last_game_at;
                        newPlayer.status = "Ongoing";
                    }

                    return newPlayer;
                }

                return p;
            }).sort((a: any, b: any) => a.last_game_at === b.last_game_at ? 0 : a.last_game_at < b.last_game_at ? 1 : -1))
        })
    }

    const startMatchPolling = () => {
        getMatch().finally(() => {
            matchTimeoutRef.current = window.setTimeout(() => {
                startMatchPolling();
            }, getNextUpdateDelaySeconds({ status: player.status, lastFinishedAt: player.last_game_at ? Math.floor(new Date(player.last_game_at).getTime() / 1000) : null }));
        });
    }
    const startOngoingPolling = () => {
        getOngoingMatch().finally(() => {
            matchTimeoutRef.current = window.setTimeout(() => {
                startOngoingPolling();
            }, getNextUpdateDelaySeconds({ status: player.status, lastFinishedAt: player.last_game_at ? Math.floor(new Date(player.last_game_at).getTime() / 1000) : null }));
        });
    }

    useEffect(() => {
        if (player.status === "Ongoing") {
            if (matchTimeoutRef.current) {
                clearTimeout(matchTimeoutRef.current);
            }

            startMatchPolling();
        } else {
            if (matchTimeoutRef.current) {
                clearTimeout(matchTimeoutRef.current);
            }

            startOngoingPolling();
        }
    }, [player.status])

    return (
        <tr className="bg-gray">
            <td className="p-3 w-32">
                <button onClick={removePlayer} className="bg-primary-text rounded-md text-white hover:text-white transition-colors px-4 py-3 flex items-center gap-2">
                    Remove
                </button>
            </td>
            <td className="p-3">
                <div className="flex items-center gap-4">
                    <img className="rounded-full" src={player.avatar} height="42" width="42" />
                    <span>
                        <a href={player.faceit_url.replace('{lang}', 'en')} target="_blank" rel="noopener noreferrer">{player.nickname}</a>
                    </span>
                </div>
            </td>
            <td className="p-3">
                <span className="text-lg flex items-center space-x-2">
                    <span ><img className="rounded-sm" width="24" height="18" style={{ width: "24px", height: "18px" }} src={`https://flagcdn.com/${player.country}.svg`} /></span>
                    <span className="w-6 h-6">
                        <FaceitLevel level={player.skill_level} />
                    </span>
                    <span className="text-lg">{player.elo} ELO</span>
                </span>
            </td>
            <td className="p-3">
                {player.ban !== null ? (

                    <span className="border-1  max-w-[300px] w-full border-pink px-3 py-2 rounded-md text-lg  items-center space-x-2">
                        <span className="text-pink text-base">

                            {player.ban.toUpperCase()}
                        </span>
                    </span>
                ) : null}
            </td>
            <td className="p-3">
                <div className="flex justify-end font-semibold">
                    {player.status === 'Ongoing' ? (
                        <div className="border-1 max-w-[300px] w-full border-pink bg-pink px-3 py-2 rounded-md text-lg flex justify-between  items-center space-x-2">
                            <span className="bg-pink-dark text-base w-full max-w-[125px] text-center justify-center p-2 rounded-md gap-x-2 flex items-center">
                                <span>Playing</span>
                                {game?.score ? (
                                    <span>
                                        {game.score}
                                    </span>
                                ) : null}

                            </span>
                            <div className="text-base flex items-center gap-x-2">
                                {game?.type === 'matchmaking' ? '5vs5 ranked' : game?.name}
                                <a href={game?.url} target="_blank">
                                    <Play />
                                </a>
                            </div>
                        </div>
                    ) : (
                        <span className="border-1  max-w-[300px] w-full border-pink px-3 py-2 rounded-md text-lg  items-center space-x-2">
                            <span className="text-pink text-base">Last time played:</span>
                            <span className="text-base">
                                {dayjs.utc(player.last_game_at).fromNow()}
                            </span>
                        </span>
                    )}

                </div>
            </td>
        </tr>
    );
}